import { useMemo } from 'react';
import { getDomainRestrictionsForItems } from 'reactApp/appHelpers/appHelpers';
import type { AccessStatus } from 'reactApp/components/SharingNewBiz/SharingNew.types';
import type { StorageCurrentFolder } from 'reactApp/modules/storage/storage.selectors';
import { useDownloadApp } from 'reactApp/ui/EmptyFolder/hooks/useDownloadApp';

import { type FolderStorageFlags, type IActionItem, EItemType } from './EmptyFolder.types';
import { useCoshare } from './hooks/useCoshare';
import { useCreate } from './hooks/useCreate';
import { useSelectFromCloud } from './hooks/useSelectFromCloud';
import { useUpload } from './hooks/useUpload';

type TEmptyFolderReturnType = Partial<Record<EItemType, (accessRights?: AccessStatus) => IActionItem>>;

export const useEmptyFolderContent = (
    id: string,
    folder: StorageCurrentFolder,
    publicUrl?: string,
    folderStorageFlags: FolderStorageFlags = {}
): TEmptyFolderReturnType => {
    const { isAlbums, isFamilyFolders, isVigbo } = folderStorageFlags;
    const create = useCreate();
    const upload = useUpload(folderStorageFlags);
    const coshare = useCoshare(id, publicUrl);
    const selectFromCloud = useSelectFromCloud(id, folderStorageFlags);
    const downloadApp = useDownloadApp();
    const { isDomainAndCreateRestricted, isDomainAndShareRestricted } = getDomainRestrictionsForItems(folder);

    return useMemo(() => {
        if (isVigbo) {
            return {
                [EItemType.UPLOAD]: upload,
                [EItemType.SELECT_FROM_CLOUD]: selectFromCloud,
            };
        }

        if (isAlbums) {
            return {
                [EItemType.UPLOAD]: upload,
                [EItemType.SELECT_FROM_CLOUD]: selectFromCloud,
                [EItemType.DOWNLOAD_APP]: downloadApp,
            };
        }

        if (isFamilyFolders) {
            return {
                [EItemType.UPLOAD]: upload,
                [EItemType.CREATE]: create,
            };
        }

        let items = {};

        if (!isDomainAndCreateRestricted) {
            items = {
                ...items,
                [EItemType.UPLOAD]: upload,
                [EItemType.CREATE]: create,
            };
        }

        if (!isDomainAndShareRestricted) {
            items = {
                ...items,
                [EItemType.COSHARING]: coshare,
            };
        }

        return items;
    }, [
        isVigbo,
        isAlbums,
        isFamilyFolders,
        isDomainAndCreateRestricted,
        isDomainAndShareRestricted,
        upload,
        selectFromCloud,
        downloadApp,
        create,
        coshare,
    ]);
};
